import { Lender, LIQUITY_FAMILY_LENDERS } from '@1delta/lender-registry'
import {
  aavePools,
  aaveReserves,
  compoundV2Pools,
  compoundV3Pools,
  compoundV3Reserves,
  initConfig,
  morphoPools,
  eulerVaults,
  aaveV4Spokes,
  aaveV4SpokeLenderKey,
  siloMarkets,
  siloV2LenderKey,
  siloMarketsV3,
  siloV3LenderKey,
  fluidResolvers,
  gearboxResolvers,
  dolomiteConfigs,
  midnightConfig,
  exactlyConfig,
  termConfig,
  termMarketsByChain,
  liquityLendersByChain,
  riverLendersByChain,
  inverseLendersByChain,
  coolerLendersByChain,
  flyingTulipLendersByChain,
  llamaLendLendersByChain,
  usddLendersByChain,
  skyLendersByChain,
  frankencoinLendersByChain,
  resupplyLendersByChain,
  curvanceLendersByChain,
  fraxlendLendersByChain,
  twyneLendersByChain,
  tellerConfigByChain,
  termMaxConfigByChain,
  tellerPoolsByChain,
} from '@1delta/data-sdk'
import lodash from 'lodash'
export { createMarketUid } from './marketUid'
export { lenderShortName } from './marketName'
// pull the functions you need
const { uniq } = lodash

export {
  isCompoundV3,
  isCompoundV3Type,
  isInit,
  isMorphoType,
  isMidnight,
  isTerm,
  isExactly,
  isLiquityV2,
  isLiquityFamily,
  isRiver,
  isTeller,
  isInverse,
  isCooler,
  isFlyingTulip,
  isLlamaLend,
  isUsdd,
  isSky,
  isDssType,
  isFrankencoin,
  isResupply,
  isFraxlend,
  isCurvance,
  isTwyne,
  isLista,
  isAaveV3Type,
  isAaveV2Type,
  isYLDR,
  isAaveV32Type,
  isAaveType,
  isCompoundV2Type,
  isVenusType,
  isSumerType,
  isTectonicType,
  isBenqiType,
  isKineticType,
  isMultiMarket,
  hasCrossMarginRisk,
  isTermMax,
  isEulerType,
  isAaveV4Type,
  isSiloV2Type,
  isSiloV3Type,
  isFluid,
  isGearboxV3,
  isDolomite,
} from '@1delta/lender-registry'

import { isAaveType, isCompoundV3 } from '@1delta/lender-registry'
import { Chain } from '@1delta/chain-registry'

const ENABLED_COMPOUNDS: string[] = [
  Lender.VENUS,
  Lender.VENUS_ETH,
  Lender.MOONWELL,
  Lender.ENCLABS,
  Lender.ENCLABS_LST,
  Lender.SUMER,
  Lender.TAKARA,
  Lender.TECTONIC,
  Lender.TECTONIC_VENO,
  Lender.TECTONIC_DEFI,
  Lender.KINETIC,
  Lender.KINETIC_FXRP,
  Lender.KINETIC_JOULE,
  Lender.BENQI,
  Lender.BENQI_AVALANCHE_ECOSYSTEM,
  Lender.WE_PIGGY,
  Lender.TENDER,
  Lender.LODESTAR,
  Lender.CREAM_FINANCE,
  Lender.SEGMENT,
  Lender.COMPOUND_V2,
  Lender.FLUX_FINANCE,
  Lender.CAPY_FI,
  // Long-tail forks read through a shared CompoundLens on their chain
  // (addresses/compoundV2.ts). Enabled only where a lens exists AND the
  // markets are open; the rest of the `lender-metadata` long tail stays off
  // this list — see the table in
  // `lending/public-data/compound-v2/README.md`.
  Lender.DEMETER,
  Lender.LANDER,
  Lender.FILDA,
  Lender.SOLIDLIZARD,
  // Reachable since the CompoundV2MarketLens deployment on Optimism, Linea and
  // Base (2026-08-24). Xpert is deliberately NOT here: 4 of its 6 Base markets
  // revert their own `exchangeRateStored`, so the lens reads them as zero and
  // serving them would publish a market with no exchange rate.
  Lender.IRON_BANK,
  Lender.MENDI,
  Lender.PAXO,
  Lender.SONNE,
  Lender.ASO_FINANCE,
  Lender.BLUME,
  Lender.KAWA,
  Lender.XPERT,
  // Manta and zkSync Era joined the lens rollout. KEOM_STONE is deliberately
  // absent: 1 of its 2 markets (kSTONE) exposes no exchange-rate getter at all,
  // so half the pool would publish with `exchangeRate = 0`.
  Lender.KEOM,
  Lender.REACTOR_FUSION,
]

const DISABLED_COMPOUNDS: { [chainId: string]: string[] } = {
  [Chain.ETHEREUM_MAINNET]: [Lender.CREAM_FINANCE],
  // FilDA's IoTeX deployment: no lens on that chain, and all 9 markets are
  // mint+borrow paused with `seizeGuardianPaused` set globally. Its BNB
  // deployment is open and stays enabled.
  [Chain.IOTEX_NETWORK_MAINNET]: [Lender.FILDA],
  // Xpert on Base: 4 of its 6 markets revert their OWN `exchangeRateStored`
  // (checked against two independent RPCs), so any lens reads them as zero and
  // serving them would publish markets with no exchange rate. Its Ink
  // deployment reads 5/5 clean and stays enabled.
  [Chain.BASE]: [Lender.XPERT],
}

/**
 * Lenders excluded from dispatch across all chains.
 * - ZEROLEND*: sunsetting — all pools winding down.
 * - TAKO_TAKO*: deprecated / inactive.
 * - PLOUTOS: exploited and inactive.
 */
const isExcludedLender = (lender: string): boolean => {
  if (lender === Lender.PLOUTOS) return true
  if (lender?.startsWith('ZEROLEND')) return true
  if (lender?.startsWith('TAKO_TAKO')) return true
  return false
}

export const ENABLED_EULER_V2_CHAINS: string[] = [
  Chain.ETHEREUM_MAINNET,
  Chain.BNB_SMART_CHAIN_MAINNET,
  Chain.UNICHAIN,
  Chain.MONAD_MAINNET,
  Chain.SONIC_MAINNET,
  Chain.TAC_MAINNET,
  Chain.SWELLCHAIN,
  Chain.BASE,
  Chain.PLASMA_MAINNET,
  Chain.ARBITRUM_ONE,
  Chain.AVALANCHE_C_CHAIN,
  Chain.LINEA,
  Chain.BOB,
  Chain.BERACHAIN,
  Chain.HYPEREVM,
]

export const getLendersForChain = (c: string) => {
  let lenders: string[] = []
  // aaves
  Object.entries(aavePools() ?? {}).forEach(([l, data]) => {
    const chains = Object.keys(data)
    if (chains.includes(c)) lenders.push(l)
  })

  // compound V3s

  if (compoundV3Pools()?.[c])
    lenders = [...lenders, ...Object.keys(compoundV3Pools()?.[c])]

  // init
  Object.entries(initConfig() ?? {}).forEach(([l, data]) => {
    const chains = Object.keys(data)
    if (chains.includes(c)) lenders.push(l)
  })

  // morpho
  Object.entries(morphoPools() ?? {}).forEach(([l, data]) => {
    const chains = Object.keys(data)
    if (chains.includes(c)) lenders.push(l)
  })

  // compound V2
  Object.entries(compoundV2Pools() ?? {}).forEach(([l, data]) => {
    const chains = Object.keys(data)
    if (
      !DISABLED_COMPOUNDS[c]?.includes(l) &&
      chains.includes(c) &&
      ENABLED_COMPOUNDS.includes(l)
    )
      lenders.push(l)
  })

  // euler V2
  if (ENABLED_EULER_V2_CHAINS.includes(c)) {
    Object.entries(eulerVaults() ?? {}).forEach(([l, data]) => {
      if (data[c]?.length > 0) lenders.push(l)
    })
  }

  // silo V2 — emit one per-pair lender key per (silo0, silo1) pair (mirrors
  // how Aave V4 emits per-spoke keys). Each Silo pair has its own
  // `SiloConfig` address which serves as the stable per-pair identity.
  const siloChainPairs = siloMarkets()?.[c] ?? []
  for (const market of siloChainPairs) {
    lenders.push(siloV2LenderKey(market.siloConfig))
  }

  // silo V3 — same per-pair lender key pattern as v2, but uses the
  // separate `siloMarketsV3` registry slot and `SILO_V3_<HEX>` namespace.
  const siloV3ChainPairs = siloMarketsV3()?.[c] ?? []
  for (const market of siloV3ChainPairs) {
    lenders.push(siloV3LenderKey(market.siloConfig))
  }

  // aave V4 — emit one per-spoke lender key per spoke (mirrors how
  // Morpho emits per-market keys). The data-sdk shape is now flat:
  // chainId → spokeAddrLower → entry.
  const v4ChainSpokes = aaveV4Spokes()?.[c] ?? {}
  for (const spokeAddr of Object.keys(v4ChainSpokes)) {
    lenders.push(aaveV4SpokeLenderKey(spokeAddr))
  }

  // fluid — single dispatch entry per chain. The borrow-vault fetcher
  // emits per-vault `FLUID_<chainId>_<vaultId>` buckets from one call, so we only
  // push the bare `FLUID` key (same pattern as Morpho Blue).
  if (fluidResolvers()?.[c]) {
    lenders.push(Lender.FLUID)
  }

  // gearbox v3 — single dispatch entry per chain. The parser fans out to
  // per-CreditManager `GEARBOX_V3_<CM_HEX>` keys from one MarketCompressor
  // call (multi-market pattern, same as Fluid / Morpho Blue). Chain is
  // supported if at least one MarketConfigurator is registered for it.
  if (gearboxResolvers()?.chains?.[c]?.marketConfigurators) {
    lenders.push(Lender.GEARBOX_V3)
  }

  // dolomite — single global cross-margin pool per chain. One `DOLOMITE` key
  // with many asset markets (Aave-like, NOT per-market keys). Supported if the
  // DolomiteMargin core is configured for the chain.
  if (dolomiteConfigs()?.[c]?.dolomiteMargin) {
    lenders.push(Lender.DOLOMITE)
  }

  // morpho midnight — single dispatch entry per chain. The public-data fetcher
  // fans out to per-market `MORPHO_MIDNIGHT_<id>` keys from one API pass
  // (multi-market pattern, same as Morpho Blue / Fluid). Supported wherever a
  // Midnight deployment is configured.
  if (midnightConfig()?.[c]?.midnight) {
    lenders.push(Lender.MORPHO_MIDNIGHT)
  }

  // term finance — single dispatch entry per chain. The public-data fetcher
  // fans out to per-repo `TERM_FINANCE_<id>` keys (multi-market pattern, same
  // as Midnight). Gated on BOTH the protocol config AND a non-empty repo set:
  // config/term-finance.json lists several chains, but only chains with live
  // repos in data/term-finance-markets.json produce data — skipping the rest
  // avoids pointless subgraph round-trips. This push is what feeds the
  // yield-tracer batch fetcher (it discovers lenders via getLendersForChain).
  if (termConfig()?.[c]?.termController && termMarketsByChain(c).length > 0) {
    lenders.push(Lender.TERM_FINANCE)
  }

  // exactly — single dispatch entry per chain. The public-data fetcher fans
  // out to per-market `EXACTLY_<MARKET_ADDR>` keys from one on-chain Previewer
  // pass (multi-market pattern, same as Midnight). Supported wherever an
  // Exactly deployment is configured.
  if (exactlyConfig()?.[c]?.previewer) {
    lenders.push(Lender.EXACTLY)
  }

  // flying tulip — single dispatch entry per chain (Ethereum + Sonic). NO
  // fan-out: `FLYING_TULIP` is both the dispatch key and the market key
  // (cross-margin, one PositionsManager per chain); one entry per asset lives
  // inside it. Gated on BOTH published lender-metadata files (config + the
  // log-discovered roster) — there is no built-in seed.
  for (const l of flyingTulipLendersByChain(c)) {
    lenders.push(l as Lender)
  }

  // liquity family — one dispatch entry PER DEPLOYMENT per chain (Liquity V2
  // and each friendly fork carry their own bare key; deviations live in the
  // metadata config row). The public-data fetcher fans out to per-branch
  // `<KEY>_<chainId>_<COLL_INDEX>` keys from one on-chain multicall.
  for (const l of liquityLendersByChain(c)) {
    lenders.push(l as Lender)
  }

  // river — single dispatch entry per chain (Satoshi/Prisma-style CDP behind
  // the SatoshiXApp diamond). Fans out to per-TroveManager `RIVER_<chainId>_<IDX>` keys.
  for (const l of riverLendersByChain(c)) {
    lenders.push(l as Lender)
  }

  // inverse — single dispatch entry (Ethereum-only FiRM CDP). Fans out to
  // per-Market `INVERSE_<MARKET_ADDR>` keys in the converter.
  for (const l of inverseLendersByChain(c)) {
    lenders.push(l as Lender)
  }

  // cooler — single dispatch entry (Ethereum-only Olympus Cooler V2). NO
  // fan-out: `COOLER` is both the dispatch key and the market key — one
  // monolithic MonoCooler market ever (gOHM → the live debt token). Gated on
  // the config row (built-in Ethereum seed, published metadata wins).
  for (const l of coolerLendersByChain(c)) {
    lenders.push(l as Lender)
  }

  // llamalend — single dispatch entry per chain (Curve's isolated two-asset
  // markets). Fans out to per-Controller `LLAMALEND_<CONTROLLER_ADDR>` keys in
  // the converter, covering BOTH generations from one entry: they share the
  // key space and one API call returns them together. Gated on a factory
  // being present in the config row.
  for (const l of llamaLendLendersByChain(c)) {
    lenders.push(l as Lender)
  }

  // usdd — single dispatch entry per chain (Maker-fork CDP). Fans out to
  // per-ilk `USDD_<chainId>_<ILK>` keys in the converter. Gated on the
  // CONFIG row (vat), not on markets — the roster is empty until USDD
  // governance files an EVM ilk (see USDD.md), and the empty fetch is
  // one early return, so keeping the entry visible costs nothing and makes
  // the activation a pure metadata change.
  for (const l of usddLendersByChain(c)) {
    lenders.push(l as Lender)
  }

  // sky — the ORIGINAL MakerDAO on the same generic dss provider. Single
  // dispatch entry per chain, fanning out to per-ilk `SKY_<chainId>_<ILK>`
  // keys. Kept as its own fan-out (not merged with USDD's) because the two
  // brands are independent deployments with independent key spaces.
  for (const l of skyLendersByChain(c)) {
    lenders.push(l as Lender)
  }

  // frankencoin — single dispatch entry per chain (ZCHF position protocol).
  // Fans out to per-ORIGINAL `FRANKENCOIN_<chainId>_<ADDR>` keys in the
  // converter. Gated on the MintingHub config row.
  for (const l of frankencoinLendersByChain(c)) {
    lenders.push(l as Lender)
  }

  // resupply — single dispatch entry per chain (reUSD CDP over other lenders'
  // supply positions). Fans out to per-pair `RESUPPLY_<chainId>_<PAIR_ADDR>`
  // keys in the converter. Gated on the REGISTRY, which is also where the
  // public-data fetcher discovers the pairs — there is no roster to publish.
  for (const l of resupplyLendersByChain(c)) {
    lenders.push(l as Lender)
  }

  // curvance — single dispatch entry per chain (Monad-only isolated two-token
  // markets). Fans out to per-market `CURVANCE_<chainId>_<MARKET_MANAGER>` keys
  // in the converter. Gated on the CENTRAL REGISTRY, which is also where the
  // public-data fetcher discovers the markets — there is no roster to publish.
  for (const l of curvanceLendersByChain(c)) {
    lenders.push(l as Lender)
  }

  // fraxlend — single dispatch entry per chain (Ethereum-only isolated pairs).
  // Fans out to per-pair `FRAXLEND_<chainId>_<PAIR_ADDR>` keys in the
  // converter. Gated on a NON-EMPTY curated roster, which is the whole
  // discovery mechanism here: unlike Resupply/Curvance there is no on-chain
  // registry to fall back to (the deployer mixes in FraxlendV1 contracts and
  // Peapods test pairs), so an empty allowlist must publish nothing.
  //
  // This push is what feeds every DISCOVERY-driven caller — the yield-tracer
  // batch fetcher and the chain-wide public/user data paths all enumerate
  // lenders through `getLendersForChain`. Without it Fraxlend still works when
  // a caller names a `FRAXLEND_1_<PAIR>` key explicitly, which is exactly what
  // the tests do, so the gap is invisible to them and total in production.
  for (const l of fraxlendLendersByChain(c)) {
    lenders.push(l as Lender)
  }

  // twyne — single dispatch entry per chain (credit delegation over Aave V3 /
  // Euler V2). Fans out to per-market
  // `TWYNE_<chainId>_<INTERMEDIATE_VAULT>_<TARGET_ASSET>` keys in the converter.
  // Gated on the COLLATERAL VAULT FACTORY, the one address every flow needs.
  //
  // Same lesson as Fraxlend above, and worth repeating because it is invisible
  // to tests: the public/user fetchers work perfectly when a caller names a
  // `TWYNE_1_<IV>_<ASSET>` key explicitly, which is what every unit and live
  // check does — so without this push Twyne would be fully built and fully
  // absent from production, where every consumer (the yield-tracer batch
  // fetcher, the chain-wide public and user data paths) discovers lenders
  // through `getLendersForChain`.
  for (const l of twyneLendersByChain(c)) {
    lenders.push(l as Lender)
  }

  // teller — single dispatch entry per chain. The public-data fetcher fans out
  // to per-pool `TELLER_<POOL_ADDR>` keys from one on-chain multicall over the
  // curated pool list (multi-market pattern, same as Midnight/Term/Exactly).
  // Gated on BOTH the protocol config AND a non-empty pool set — this push is
  // what feeds the yield-tracer batch fetcher (it discovers lenders via
  // getLendersForChain), so without it Teller markets never get fetched.
  if (tellerConfigByChain(c)?.tellerV2 && tellerPoolsByChain(c).length > 0) {
    lenders.push(Lender.TELLER)
  }

  // termmax — single dispatch entry per chain. The public-data fetcher fans out
  // to per-market `TERMMAX_<MARKET_ADDR>` keys from ONE `/market/data` call
  // (multi-market pattern, same as Midnight/Term/Exactly/Teller).
  //
  // Gated ONLY on the chain config, deliberately NOT on a market list: TermMax
  // markets are DISCOVERED at fetch time (they roll with every maturity and
  // matured ones vanish upstream), so there is no static set to count here the
  // way Teller/Term gate on theirs. This push is what feeds the yield-tracer
  // batch fetcher — without it TERMMAX never enters the sweep at all, no matter
  // how the API routing is wired.
  if (termMaxConfigByChain(c)?.viewer) {
    lenders.push(Lender.TERMMAX)
  }

  return lenders.filter((l) => !isExcludedLender(l))
}

/** Filter lenders by protocol list */
export const filterLendersByProtocol = (
  allLenders: string[],
  protocolList?: string[],
): string[] => {
  // return all lenders if no filter is provided
  if (!protocolList || protocolList.length === 0) {
    return allLenders
  }

  const hasMorpho = protocolList.includes(Lender.MORPHO_BLUE)
  const hasMoolah = protocolList.includes(Lender.LISTA_DAO)
  const hasFluid = protocolList.includes(Lender.FLUID)
  const hasGearboxV3 = protocolList.includes(Lender.GEARBOX_V3)
  const hasDolomite = protocolList.includes(Lender.DOLOMITE)
  // Liquity family: a bare deployment key in the filter matches its
  // per-branch `<KEY>_<chainId>_<COLL_INDEX>` keys (each fork brand independently).
  const liquityBases = LIQUITY_FAMILY_LENDERS.filter((b) =>
    protocolList.includes(b),
  )
  // Filter lenders based on protocol list
  return allLenders.filter(
    (lender) =>
      protocolList.includes(lender) ||
      (hasMorpho && lender?.startsWith('MORPHO_BLUE')) ||
      (hasMoolah && lender?.startsWith('LISTA_DAO')) ||
      (hasFluid && lender?.startsWith('FLUID')) ||
      (hasGearboxV3 && lender?.startsWith('GEARBOX_V3')) ||
      (hasDolomite && lender?.startsWith('DOLOMITE')) ||
      liquityBases.some((b) => lender?.startsWith(b)) ||
      (protocolList.includes(Lender.RIVER) && lender?.startsWith('RIVER')) ||
      (protocolList.includes(Lender.TELLER) && lender?.startsWith('TELLER')) ||
      (protocolList.includes(Lender.USDD) && lender?.startsWith('USDD')) ||
      (protocolList.includes(Lender.SKY) && lender?.startsWith('SKY')) ||
      (protocolList.includes(Lender.FRANKENCOIN) &&
        lender?.startsWith('FRANKENCOIN')),
  )
}

/** All aave protocols per chain */
export const getAavesForChain = () => {
  let lenders: { [c: string]: string[] } = {}
  Object.entries(aavePools() ?? {}).forEach(([l, data]) => {
    Object.entries(data).forEach(([chain, data]) => {
      if (!lenders[chain]) lenders[chain] = []
      lenders[chain] = uniq([...lenders[chain], l])
    })
  })
  return lenders
}

export const getLenderAssets = (
  chainId: string | number | undefined,
  lendingProtocol: string = Lender.AAVE_V3,
): string[] => {
  if (!chainId) return []
  if (isAaveType(lendingProtocol))
    return aaveReserves()?.[lendingProtocol]?.[chainId] ?? []
  if (isCompoundV3(lendingProtocol))
    return compoundV3Reserves()?.[lendingProtocol]?.[chainId] ?? []
  return []
}
